import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useState, useEffect, useCallback } from 'react';
import { 
  getAdminMonitoringData, 
  getActivePhases, 
  uploadF100File as uploadF100FileService,
  markRequestAsPaid,
  getPhasesList,
  updateActivePhase,
  sendDataUploadNotification,
  getCropById,
  getInvoiceDetails,
  updateInvoiceDetails,
  generateInvoicePdf,
  submitInvoiceAndSendEmail,
  MonitoringAdminData, 
  ActivePhase,
  AttachedLink,
  Phase,
  CropData,
  InvoiceDetails,
  PaginationMeta
} from './monitoringAdminService';
import { useTranslation } from 'react-i18next';

/**
 * Hook to fetch and manage admin monitoring data
 */
export function useAdminMonitoringData() {
  const [data, setData] = useState<MonitoringAdminData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activePhase, setActivePhase] = useState<ActivePhase>({ name: '', phases_id: '', monitoring_id: '', attachedLink: [] });
  const [isLoadingPhase, setIsLoadingPhase] = useState<boolean>(false);
  const [phasesList, setPhasesList] = useState<Phase[]>([]);
  const [isLoadingPhasesList, setIsLoadingPhasesList] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationMeta>({
    total: 0,
    page: 1,
    per_page: 10,
    total_pages: 0
  });
  const { t } = useTranslation();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const fetchData = useCallback(async (page: number = 1, perPage: number = 10, search?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAdminMonitoringData(page, perPage, search);
      setData(result.data);
      setPagination(result.meta);
    } catch (err) {
      console.error('Error fetching admin monitoring data:', err);
      setError('Error fetching data');
      showSnackbar('error', 'Error fetching data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar]);

  // Refresh monitoring data with pagination support
  const refreshMonitoring = useCallback((page?: number, perPage?: number, search?: string) => {
    return fetchData(
      page !== undefined ? page : pagination.page, 
      perPage !== undefined ? perPage : pagination.per_page,
      search
    );
  }, [fetchData, pagination.page, pagination.per_page]);

  const fetchActivePhase = useCallback(async (phasesId: string) => {
    setIsLoadingPhase(true);
    try {
      const result = await getActivePhases(phasesId);
      
      // Ensure we have a valid result object
      if (result && typeof result === 'object' && result.name && result.phases_id) {
        setActivePhase(result);
        return result;
      } else {
        showSnackbar('error', 'Invalid phase data received', 'error');
        return { name: '', phases_id: '', monitoring_id: '', attachedLink: [] };
      }
    } catch (err) {
      console.error('Error fetching active phase:', err);
      showSnackbar('error', 'Error fetching active phase', 'error');
      return { name: '', phases_id: '', monitoring_id: '', attachedLink: [] };
    } finally {
      setIsLoadingPhase(false);
    }
  }, [showSnackbar]);

  const uploadF100File = useCallback(async (phasesId: string, fileKey: string, farmerId: string, requestId: string) => {
    try {
      // The fileKey should contain the complete file data including key, uploadId, and parts
      await uploadF100FileService(phasesId, fileKey, farmerId, requestId);
      showSnackbar('success', 'F100 file uploaded successfully', 'success');
      // Refresh data after update
      refreshMonitoring();
      return true;
    } catch (err) {
      console.error('Error uploading F100 file:', err);
      showSnackbar('error', 'Error uploading F100 file', 'error');
      return false;
    }
  }, [refreshMonitoring, showSnackbar]);

  const markAsPaid = useCallback(async (phasesId: string) => {
    try {
      await markRequestAsPaid(phasesId);
      showSnackbar('success', 'Request marked as paid successfully', 'success');
      // Refresh data after update
      refreshMonitoring();
      return true;
    } catch (err) {
      console.error('Error marking request as paid:', err);
      showSnackbar('error', 'Error marking request as paid', 'error');
      return false;
    }
  }, [refreshMonitoring, showSnackbar]);

  const updatePhase = useCallback(async (phasesId: string, monitoringId: string) => {
    try {
      await updateActivePhase(phasesId, monitoringId);
      showSnackbar('success', 'Phase updated successfully', 'success');
      // Refresh data after update
      refreshMonitoring();
      return true;
    } catch (err) {
      console.error('Error updating phase:', err);
      showSnackbar('error', 'Error updating phase', 'error');
      return false;
    }
  }, [refreshMonitoring, showSnackbar]);

  const fetchPhasesList = useCallback(async (phasesId: string) => {
    setIsLoadingPhasesList(true);
    try {
      const result = await getPhasesList(phasesId);
      setPhasesList(result);
      return result;
    } catch (err) {
      console.error('Error fetching phases list:', err);
      showSnackbar('error', 'Error fetching phases list', 'error');
      return [];
    } finally {
      setIsLoadingPhasesList(false);
    }
  }, [showSnackbar]);

  const notifyDataUpload = useCallback(async (phasesId: string) => {
    try {
      await sendDataUploadNotification(phasesId);
      showSnackbar('success', 'Data upload notification sent successfully', 'success');
      // Refresh data after notification
      refreshMonitoring();
      return true;
    } catch (err) {
      console.error('Error sending data upload notification:', err);
      showSnackbar('error', 'Error sending data upload notification', 'error');
      return false;
    }
  }, [refreshMonitoring, showSnackbar]);

  return { 
    data, 
    isLoading, 
    error, 
    refreshData: refreshMonitoring, 
    fetchActivePhase,
    activePhase,
    isLoadingPhase,
    uploadF100File,
    markAsPaid,
    fetchPhasesList,
    phasesList,
    isLoadingPhasesList,
    updatePhase,
    notifyDataUpload,
    pagination
  };
}

/**
 * Hook to fetch crop data by ID
 */
export function useGetCropById() {
  const [cropData, setCropData] = useState<CropData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  
  // Add a cache to prevent duplicate API calls
  const [cropCache, setCropCache] = useState<Record<string, CropData | null>>({});
  
  // Queue for batching requests
  const [requestQueue, setRequestQueue] = useState<string[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState<boolean>(false);

  // Process the queue of crop IDs in batches
  const processBatchQueue = useCallback(async () => {
    if (isProcessingQueue || requestQueue.length === 0) {
      return;
    }
    
    setIsProcessingQueue(true);
    
    try {
      // Get unique IDs that are not in cache
      const uniqueIds = [...new Set(requestQueue)].filter(id => !cropCache[id]);
      
      if (uniqueIds.length === 0) {
        setRequestQueue([]);
        setIsProcessingQueue(false);
        return;
      }
      
      // Fetch data for all IDs in one request
      const results = await getCropById(uniqueIds);
      
      if (Array.isArray(results)) {
        // Update cache with results
        const newCache = { ...cropCache };
        
        results.forEach(crop => {
          if (crop && crop.id) {
            newCache[crop.id] = crop;
          }
        });
        
        setCropCache(newCache);
      }
      
      // Clear processed IDs from queue
      setRequestQueue([]);
    } catch (err) {
      console.error('Error processing crop batch:', err);
      setError('Error fetching crop data');
    } finally {
      setIsProcessingQueue(false);
    }
  }, [requestQueue, isProcessingQueue, cropCache]);
  
  // Effect to process queue when it changes
  useEffect(() => {
    if (requestQueue.length > 0) {
      processBatchQueue();
    }
  }, [requestQueue, processBatchQueue]);

  // Fetch a single crop by ID
  const fetchCropById = useCallback(async (cropId: string) => {
    if (!cropId) {
      return null;
    }
    
    // Check cache first
    if (cropCache[cropId]) {
      return cropCache[cropId];
    }
    
    // Add to request queue for batch processing
    setRequestQueue(prev => [...prev, cropId]);
    
    // Wait for processing to complete
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (cropCache[cropId]) {
        return cropCache[cropId];
      }
      
      attempts++;
    }
    
    // If still not in cache after waiting, fetch individually
    setIsLoading(true);
    
    try {
      const result = await getCropById(cropId) as CropData | null;
      
      if (result) {
        setCropCache(prev => ({
          ...prev,
          [cropId]: result
        }));
      }
      
      return result;
    } catch (err) {
      console.error('Error fetching individual crop:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [cropCache, requestQueue]);

  return { cropData, isLoading, error, fetchCropById };
}

/**
 * Hook to manage invoice details for a monitoring record
 */
export function useInvoiceDetails() {
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState<boolean>(false);
  const { t } = useTranslation();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const fetchInvoiceDetails = useCallback(async (monitoringId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getInvoiceDetails(monitoringId);
      setInvoiceDetails(result);
      return result;
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setError('Error fetching invoice details');
      showSnackbar('error', 'Error fetching invoice details', 'error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar]);

  const saveInvoiceDetails = useCallback(async (monitoringId: string, details: InvoiceDetails) => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await updateInvoiceDetails(monitoringId, details);
      if (success) {
        setInvoiceDetails(details);
        showSnackbar('success', 'Invoice details updated successfully', 'success');
        return true;
      } else {
        showSnackbar('error', 'Failed to update invoice details', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error updating invoice details:', err);
      setError('Error updating invoice details');
      showSnackbar('error', 'Error updating invoice details', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar]);

  const generatePdf = useCallback(async (monitoringId: string, details: InvoiceDetails) => {
    setIsGeneratingPdf(true);
    setPdfUrl(null);
    try {
      // Determine which name and ID to use based on company information
      const firstName = details.representative_first_name && details.company_id_number ? 
        details.representative_first_name : details.first_name;
      
      const lastName = details.representative_last_name && details.company_id_number ? 
        details.representative_last_name : details.last_name;
      
      const idNumber = details.company_id_number || details.id_number;

      const invoiceData = {
        invoice_id: details.id,
        first_name: firstName,
        last_name: lastName,
        id_number: idNumber,
        price: details.price
      };

      const url = await generateInvoicePdf(monitoringId, invoiceData);
      
      if (url) {
        setPdfUrl(url);
        return url;
      } else {
        showSnackbar('error', 'Failed to generate invoice PDF', 'error');
        return null;
      }
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      showSnackbar('error', 'Error generating invoice PDF', 'error');
      return null;
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [showSnackbar]);

  const submitInvoice = useCallback(async (monitoringId: string, invoiceId: string, pdfLink: string, price?: number) => {
    if (!pdfLink) {
      showSnackbar('error', 'No PDF link available. Please generate an invoice first.', 'error');
      return false;
    }

    setIsSubmittingInvoice(true);
    try {
      // Use the provided price parameter or fall back to the one from invoiceDetails
      const priceToUse = price !== undefined ? price : invoiceDetails?.price;
      
      const success = await submitInvoiceAndSendEmail(monitoringId, invoiceId, pdfLink, priceToUse);
      if (success) {
        showSnackbar('success', 'Invoice submitted and email sent successfully', 'success');
        return true;
      } else {
        showSnackbar('error', 'Failed to submit invoice and send email', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error submitting invoice and sending email:', err);
      showSnackbar('error', 'Error submitting invoice and sending email', 'error');
      return false;
    } finally {
      setIsSubmittingInvoice(false);
    }
  }, [showSnackbar, invoiceDetails]);

  return {
    invoiceDetails,
    setInvoiceDetails,
    isLoading,
    error,
    fetchInvoiceDetails,
    saveInvoiceDetails,
    generatePdf,
    pdfUrl,
    setPdfUrl,
    isGeneratingPdf,
    submitInvoice,
    isSubmittingInvoice
  };
} 